import React, {
  useEffect,
  useState,
} from 'react';

import {
  useTranslation,
  Trans,
} from 'react-i18next';

import { useDispatch } from 'react-redux';

import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';

import {
  clientAuthApi,
} from '../services/clientAuthApi';

import {
  loginSuccess,
} from '../store/slices/authSlice';

import {
  cleanDateForApi,
  formatPhoneAsYouType,
  getMaxDateForInput,
  isMinor,
  parseInternationalPhone,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';

import type {
  ClientData,
} from './CustomerDetailsStep';

import {
  LegalPrivacyLabel,
} from './LegalConsentLabels';

import {
  InternationalPhoneInput,
} from './InternationalPhoneInput';

import {
  ModalHeader,
} from './ModalHeader';

import {
  LegalPageContent,
} from '../pages/LegalInfoPage';

type LegalGuardianData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;

  relationshipToMinor:
    | 'parent'
    | 'legal_guardian';

  relationshipDetails?: string;
};

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor: boolean | null;

  password: string;
  confirmPassword: string;

  acceptTerms: boolean;
  acceptPrivacy: boolean;

  legalGuardian:
    | LegalGuardianData
    | null;
};

type Props = {
  onSuccess: (
    clientData: ClientData
  ) => void;

  onCancel?: () => void;

  preferredLanguage?:
    | 'fr'
    | 'en';

  initialValues?: Partial<
    Pick<
      FormState,
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'phone'
      | 'dateOfBirth'
    >
  >;

  /**
   * When tattoo,
   * minors are rejected outright.
   */
  bookingType?:
    | 'tattoo'
    | 'piercing';
};

const PASSWORD_PATTERN =
  /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/;

const getInitialPhoneState = (
  phone: string
) => {
  const parsedPhone =
    parseInternationalPhone(
      phone,
      'FR'
    );

  const country =
    parsedPhone.country ??
    'FR';

  return {
    country,

    display:
      formatPhoneAsYouType(
        phone,
        country
      ),

    e164:
      parsedPhone.isValid
        ? parsedPhone.e164
        : null,

    isValid:
      parsedPhone.isValid,
  };
};

export function AccountCreationForm({
  onSuccess,
  onCancel,
  preferredLanguage = 'fr',
  initialValues,
  bookingType,
}: Props) {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();

  /*
   * =========================================================
   * FORM
   * =========================================================
   */

  const [
    formData,
    setFormData,
  ] = useState<FormState>({
    firstName:
      initialValues?.firstName ||
      '',

    lastName:
      initialValues?.lastName ||
      '',

    email:
      initialValues?.email ||
      '',

    phone:
      initialValues?.phone ||
      '',

    dateOfBirth:
      initialValues?.dateOfBirth ||
      '',
    isMinor: null,

    password: '',
    confirmPassword: '',

    acceptTerms: false,
    acceptPrivacy: false,

    legalGuardian: null,
  });

  const [
    errors,
    setErrors,
  ] = useState<
    Record<
      string,
      string
    >
  >({});

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  /*
   * =========================================================
   * LEGAL MODAL
   * =========================================================
   */

  const [
    openLegalModal,
    setOpenLegalModal,
  ] = useState<
    'terms' | null
  >(null);

  /*
   * Même fonctionnement que PiercingBooking :
   *
   * - empêche le scroll derrière la popup
   * - fermeture avec Escape
   */
  useEffect(() => {
    if (!openLegalModal) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (
        event.key ===
        'Escape'
      ) {
        setOpenLegalModal(
          null
        );
      }
    };

    document.body.style.overflow =
      'hidden';

    document.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [openLegalModal]);

  /*
   * =========================================================
   * CLIENT PHONE
   * =========================================================
   */

  const [
    initialPhoneState,
  ] = useState(
    () =>
      getInitialPhoneState(
        initialValues?.phone ||
          ''
      )
  );

  const [
    contactPhoneCountry,
    setContactPhoneCountry,
  ] =
    useState<SupportedPhoneCountry>(
      initialPhoneState.country
    );

  const [
    contactPhoneDisplay,
    setContactPhoneDisplay,
  ] = useState(
    initialPhoneState.display
  );

  const [
    contactPhoneE164,
    setContactPhoneE164,
  ] = useState<
    string | null
  >(
    initialPhoneState.e164
  );

  const [
    contactPhoneIsValid,
    setContactPhoneIsValid,
  ] = useState(
    initialPhoneState.isValid
  );

  /*
   * =========================================================
   * GUARDIAN PHONE
   * =========================================================
   */

  const [
    guardianPhoneCountry,
    setGuardianPhoneCountry,
  ] =
    useState<SupportedPhoneCountry>(
      'FR'
    );

  const [
    guardianPhoneDisplay,
    setGuardianPhoneDisplay,
  ] = useState('');

  const [
    guardianPhoneE164,
    setGuardianPhoneE164,
  ] = useState<
    string | null
  >(null);

  const [
    guardianPhoneIsValid,
    setGuardianPhoneIsValid,
  ] = useState(false);

  /*
   * =========================================================
   * HELPERS
   * =========================================================
   */

  const removeError = (
    field: string
  ) => {
    setErrors(
      (
        previous
      ) => {
        if (
          !previous[field]
        ) {
          return previous;
        }

        const next = {
          ...previous,
        };

        delete next[field];

        return next;
      }
    );
  };

  const setFieldError = (
    field: string,
    message:
      | string
      | null
  ) => {
    setErrors(
      (
        previous
      ) => {
        const next = {
          ...previous,
        };

        if (message) {
          next[field] =
            message;
        } else {
          delete next[field];
        }

        return next;
      }
    );
  };

  const handleChange = (
    field: keyof FormState,
    value:
      | string
      | boolean
      | LegalGuardianData
      | null
  ) => {
    setFormData(
      (
        previous
      ) => ({
        ...previous,
        [field]:
          value,
      })
    );

    removeError(
      field
    );
  };

  /*
   * =========================================================
   * LIVE PASSWORD VALIDATION
   * =========================================================
   */

  const getPasswordError = (
    value: string,
    required = true
  ):
    | string
    | null => {
    if (!value) {
      return required
        ? t(
            'auth.register.errors.required'
          )
        : null;
    }

    if (
      value.length < 8
    ) {
      return t(
        'auth.register.errors.passwordWeak'
      );
    }

    if (
      !PASSWORD_PATTERN.test(
        value
      )
    ) {
      return t(
        'auth.register.errors.passwordWeak'
      );
    }

    return null;
  };

  const handlePasswordChange = (
    value: string
  ) => {
    setFormData(
      (
        previous
      ) => ({
        ...previous,
        password:
          value,
      })
    );

    /*
     * Validation immédiate.
     *
     * Tant que l'utilisateur n'a rien saisi,
     * on évite d'afficher "requis".
     *
     * Dès le premier caractère :
     * robustesse vérifiée immédiatement.
     */
    setFieldError(
      'password',
      getPasswordError(
        value,
        false
      )
    );

    /*
     * Si la confirmation a déjà été saisie,
     * on la revalide immédiatement lorsque
     * le premier mot de passe change.
     */
    if (
      formData.confirmPassword
    ) {
      setFieldError(
        'confirmPassword',
        value ===
          formData.confirmPassword
          ? null
          : t(
              'auth.register.errors.passwordMismatch'
            )
      );
    }
  };

  const handleConfirmPasswordChange =
    (
      value: string
    ) => {
      setFormData(
        (
          previous
        ) => ({
          ...previous,
          confirmPassword:
            value,
        })
      );

      /*
       * Validation immédiate de la
       * correspondance des deux mots
       * de passe.
       */
      if (!value) {
        setFieldError(
          'confirmPassword',
          null
        );

        return;
      }

      setFieldError(
        'confirmPassword',
        value ===
          formData.password
          ? null
          : t(
              'auth.register.errors.passwordMismatch'
            )
      );
    };

  /*
   * =========================================================
   * MINOR / GUARDIAN
   * =========================================================
   */

  const clientIsMinor =
    formData.dateOfBirth
      ? isMinor(
          formData.dateOfBirth
        )
      : formData.isMinor;

  const tattooMinorBlocked =
    bookingType ===
      'tattoo' &&
    clientIsMinor;

  const showGuardian =
    clientIsMinor &&
    !tattooMinorBlocked;

  /*
   * Initialise ou retire le représentant
   * légal selon l'âge.
   */
  useEffect(() => {
    if (
      showGuardian &&
      !formData.legalGuardian
    ) {
      setFormData(
        (
          previous
        ) => ({
          ...previous,

          legalGuardian: {
            firstName:
              '',

            lastName:
              '',

            email:
              '',

            phone:
              '',

            relationshipToMinor:
              'parent',

            relationshipDetails:
              '',
          },
        })
      );

      setGuardianPhoneCountry(
        'FR'
      );

      setGuardianPhoneDisplay(
        ''
      );

      setGuardianPhoneE164(
        null
      );

      setGuardianPhoneIsValid(
        false
      );
    } else if (
      !showGuardian &&
      formData.legalGuardian
    ) {
      setFormData(
        (
          previous
        ) => ({
          ...previous,
          legalGuardian:
            null,
        })
      );

      setGuardianPhoneCountry(
        'FR'
      );

      setGuardianPhoneDisplay(
        ''
      );

      setGuardianPhoneE164(
        null
      );

      setGuardianPhoneIsValid(
        false
      );

      setErrors(
        (
          previous
        ) => {
          const next = {
            ...previous,
          };

          delete next[
            'legalGuardian.firstName'
          ];

          delete next[
            'legalGuardian.lastName'
          ];

          delete next[
            'legalGuardian.email'
          ];

          delete next[
            'legalGuardian.phone'
          ];

          return next;
        }
      );
    }
  }, [
    showGuardian,
    formData.legalGuardian,
  ]);

  /*
   * =========================================================
   * VALIDATION COMPLETE
   * =========================================================
   */

  const validate =
    () => {
      const nextErrors: Record<
        string,
        string
      > = {};

      /*
       * First name
       */
      if (
        !formData.firstName.trim()
      ) {
        nextErrors.firstName =
          t(
            'auth.register.errors.required'
          );
      }

      /*
       * Email
       */
      if (
        !formData.email.trim()
      ) {
        nextErrors.email =
          t(
            'auth.register.errors.required'
          );
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          formData.email.trim()
        )
      ) {
        nextErrors.email =
          t(
            'auth.register.errors.invalidEmail'
          );
      }

      /*
       * Client phone
       */
      if (
        contactPhoneDisplay.trim() &&
        (
        !contactPhoneIsValid ||
        !contactPhoneE164
        )
      ) {
        nextErrors.phone =
          t(
            'auth.register.errors.invalidPhone'
          );
      }

      /*
       * Password
       */
      const passwordError =
        getPasswordError(
          formData.password
        );

      if (
        passwordError
      ) {
        nextErrors.password =
          passwordError;
      }

      /*
       * Confirm password
       */
      if (
        !formData.confirmPassword
      ) {
        nextErrors.confirmPassword =
          t(
            'auth.register.errors.required'
          );
      } else if (
        formData.password !==
        formData.confirmPassword
      ) {
        nextErrors.confirmPassword =
          t(
            'auth.register.errors.passwordMismatch'
          );
      }

      /*
       * Terms
       */
      if (
        !formData.acceptTerms
      ) {
        nextErrors.acceptTerms =
          t(
            'auth.register.errors.termsRequired'
          );
      }

      /*
       * Privacy
       */
      if (
        !formData.acceptPrivacy
      ) {
        nextErrors.acceptPrivacy =
          t(
            'auth.register.errors.privacyRequired'
          );
      }

      /*
       * Legal guardian
       */
      if (
        showGuardian &&
        formData.legalGuardian
      ) {
        if (
          formData.legalGuardian.email.trim() &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
            formData
              .legalGuardian
              .email
              .trim()
          )
        ) {
          nextErrors[
            'legalGuardian.email'
          ] =
            t(
              'customerDetails.guest.errors.guardianEmailInvalid'
            );
        }

        /*
         * Nouveau :
         * validation InternationalPhoneInput
         */
        if (
          guardianPhoneDisplay.trim() &&
          (
          !guardianPhoneIsValid ||
          !guardianPhoneE164
          )
        ) {
          nextErrors[
            'legalGuardian.phone'
          ] =
            t(
              'auth.register.errors.invalidPhone'
            );
        }
      }

      setErrors(
        nextErrors
      );

      return (
        Object.keys(
          nextErrors
        ).length === 0
      );
    };

  /*
   * =========================================================
   * SUBMIT
   * =========================================================
   */

  const handleSubmit =
    async () => {
      if (
        !validate()
      ) {
        return;
      }

      const phoneE164 =
        contactPhoneE164 ?? '';

      setLoading(
        true
      );

      try {
        const trimmedEmail =
          formData.email.trim();

        const response =
          await clientAuthApi.register(
            {
              firstName:
                formData.firstName.trim(),

              lastName:
                formData.lastName.trim(),

              email:
                trimmedEmail,

              phone:
                phoneE164,

              password:
                formData.password,

              dateOfBirth:
                cleanDateForApi(
                  formData.dateOfBirth
                ) ||
                formData.dateOfBirth ||
                undefined,

              isMinor:
                clientIsMinor,

              acceptTerms:
                formData.acceptTerms,

              acceptPrivacy:
                formData.acceptPrivacy,

              legalGuardian:
                showGuardian &&
                formData.legalGuardian
                  ? {
                      firstName:
                        formData
                          .legalGuardian
                          .firstName
                          .trim(),

                      lastName:
                        formData
                          .legalGuardian
                          .lastName
                          .trim(),

                      email:
                        formData
                          .legalGuardian
                          .email
                          .trim(),

                      /*
                       * IMPORTANT :
                       * on envoie maintenant
                       * le numéro E.164.
                       */
                      phone:
                        guardianPhoneE164!,

                      relationshipToMinor:
                        formData
                          .legalGuardian
                          .relationshipToMinor,

                      relationshipDetails:
                        formData
                          .legalGuardian
                          .relationshipDetails
                          ?.trim() ||
                        undefined,
                    }
                  : undefined,
            }
          );

        if (
          response.success &&
          response.user
        ) {
          let autoLoggedIn =
            false;

          try {
            const loginResponse =
              await clientAuthApi.login(
                {
                  email:
                    trimmedEmail,

                  password:
                    formData.password,
                }
              );

            if (
              loginResponse.success &&
              loginResponse.token &&
              loginResponse.refreshToken &&
              loginResponse.user
            ) {
              dispatch(
                loginSuccess({
                  user:
                    loginResponse.user,

                  token:
                    loginResponse.token,

                  refreshToken:
                    loginResponse.refreshToken,
                })
              );

              autoLoggedIn =
                true;
            }
          } catch {
            /*
             * Auto-login non bloquant :
             * le compte a bien été créé.
             */
            autoLoggedIn =
              false;
          }

          onSuccess({
            id:
              response.user.id,

            firstName:
              response.user.firstName,

            lastName:
              response.user.lastName,

            email:
              response.user.email,

            phone:
              response.user.phone,

            dateOfBirth:
              formData.dateOfBirth,

            isMinor:
              clientIsMinor,

            preferredLanguage,

            legalGuardian:
              showGuardian &&
              formData.legalGuardian
                ? {
                    firstName:
                      formData
                        .legalGuardian
                        .firstName
                        .trim(),

                    lastName:
                      formData
                        .legalGuardian
                        .lastName
                        .trim(),

                    email:
                      formData
                        .legalGuardian
                        .email
                        .trim(),

                    phone:
                      guardianPhoneE164!,

                    relationshipToMinor:
                      formData
                        .legalGuardian
                        .relationshipToMinor,

                    relationshipDetails:
                      formData
                        .legalGuardian
                        .relationshipDetails
                        ?.trim() ||
                      undefined,
                  }
                : undefined,

            accountCreated:
              true,

            autoLoggedIn,
          });
        }
      } catch (error) {
        setErrors(
          (
            previous
          ) => ({
            ...previous,

            general:
              error instanceof
              Error
                ? error.message
                : t(
                    'clientForm.error.networkError'
                  ),
          })
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <>
      <fieldset
        disabled={loading}
        className={`space-y-6 ${
          loading
            ? 'opacity-60'
            : ''
        }`}
      >
        {onCancel && (
          <button
            onClick={
              onCancel
            }
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            type="button"
          >
            <ArrowLeft className="w-4 h-4" />

            <span>
              {t(
                'customerDetails.backToOptions'
              )}
            </span>
          </button>
        )}

        {/* HEADER */}

        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold">
            {t(
              'auth.register.title'
            )}
          </h2>

          <p className="text-gray-600">
            {t(
              'auth.register.subtitle'
            )}
          </p>
        </div>

        {/* GLOBAL ERROR */}

        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {
              errors.general
            }
          </div>
        )}

        {/* TATTOO MINOR */}

        {tattooMinorBlocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
            <p className="font-semibold">
              {t(
                'auth.register.tattooMinorBlocked.title',
                'Tattoo not permitted for minors'
              )}
            </p>

            <p>
              {t(
                'auth.register.tattooMinorBlocked.description',
                'Under French law, tattooing is not allowed for minors under 18, even with parental consent. You must be at least 18 years old to book a tattoo appointment.'
              )}
            </p>
          </div>
        )}

        {/* MINOR NOTICE */}

        {clientIsMinor &&
          !tattooMinorBlocked && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p className="font-semibold">
                {t(
                  'auth.register.minorNotice.title'
                )}
              </p>

              <p>
                {t(
                  'auth.register.minorNotice.description'
                )}
              </p>
            </div>
          )}

        <p className="text-xs text-gray-500 mb-2">
          <span className="text-red-500">
            *
          </span>{' '}
          {t(
            'auth.register.requiredFields',
            'Required fields'
          )}
        </p>

        {/* CLIENT INFOS */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* FIRST NAME */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.firstName'
              )}{' '}

              <span className="text-red-500">
                *
              </span>
            </label>

            <input
              value={
                formData.firstName
              }
              onChange={(
                event
              ) =>
                handleChange(
                  'firstName',
                  event.target.value
                )
              }
              className={`input-field ${
                errors.firstName
                  ? 'border-red-300'
                  : ''
              }`}
              type="text"
            />

            {errors.firstName && (
              <p className="text-xs text-red-600 mt-1">
                {
                  errors.firstName
                }
              </p>
            )}
          </div>

          {/* LAST NAME */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.lastName'
              )} (optionnel)
            </label>

            <input
              value={
                formData.lastName
              }
              onChange={(
                event
              ) =>
                handleChange(
                  'lastName',
                  event.target.value
                )
              }
              className={`input-field ${
                errors.lastName
                  ? 'border-red-300'
                  : ''
              }`}
              type="text"
            />

            {errors.lastName && (
              <p className="text-xs text-red-600 mt-1">
                {
                  errors.lastName
                }
              </p>
            )}
          </div>

          {/* EMAIL */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.email'
              )}{' '}

              <span className="text-red-500">
                *
              </span>
            </label>

            <input
              value={
                formData.email
              }
              onChange={(
                event
              ) =>
                handleChange(
                  'email',
                  event.target.value
                )
              }
              className={`input-field ${
                errors.email
                  ? 'border-red-300'
                  : ''
              }`}
              type="email"
            />

            {errors.email && (
              <p className="text-xs text-red-600 mt-1">
                {
                  errors.email
                }
              </p>
            )}
          </div>

          {/* CLIENT PHONE */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.phone'
              )} (optionnel)
            </label>

            <InternationalPhoneInput
              value={
                contactPhoneDisplay
              }
              country={
                contactPhoneCountry
              }
              onChange={(
                result
              ) => {
                setContactPhoneDisplay(
                  result.displayValue
                );

                setContactPhoneCountry(
                  result.country
                );

                setContactPhoneE164(
                  result.e164Value
                );

                setContactPhoneIsValid(
                  result.isValid
                );

                setFormData(
                  (
                    previous
                  ) => ({
                    ...previous,

                    phone:
                      result.displayValue,
                  })
                );

                if (
                  result.isValid
                ) {
                  removeError(
                    'phone'
                  );
                }
              }}
              selectClassName="input-field"
              inputClassName={`input-field ${
                errors.phone
                  ? 'border-red-300'
                  : ''
              }`}
              error={
                errors.phone
              }
            />
          </div>

          {/* DATE OF BIRTH */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.dateOfBirth'
              )} (optionnel)
            </label>

            <input
              value={
                formData.dateOfBirth
              }
              disabled={
                formData.isMinor === true &&
                !formData.dateOfBirth
              }
              onChange={(event) => {
                const value = event.target.value;
                setFormData((previous) => ({
                  ...previous,
                  dateOfBirth: value,
                  isMinor: value ? isMinor(value) : null,
                }));
                removeError('dateOfBirth');
              }}
              className={`input-field ${
                errors.dateOfBirth
                  ? 'border-red-300'
                  : ''
              }`}
              type="date"
              max={
                getMaxDateForInput()
              }
            />

            {errors.dateOfBirth && (
              <p className="text-xs text-red-600 mt-1">
                {
                  errors.dateOfBirth
                }
              </p>
            )}
            <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
              <input
                type="checkbox"
                checked={clientIsMinor}
                disabled={Boolean(formData.dateOfBirth)}
                onChange={(event) =>
                  setFormData((previous) => ({
                    ...previous,
                    isMinor: event.target.checked ? true : null,
                  }))
                }
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              Mineur (optionnel)
            </label>
          </div>
        </div>

        {/*
         * =====================================================
         * PASSWORDS
         * =====================================================
         */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* PASSWORD */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.password'
              )}{' '}

              <span className="text-red-500">
                *
              </span>
            </label>

            <div className="relative">
              <input
                value={
                  formData.password
                }
                onChange={(
                  event
                ) =>
                  handlePasswordChange(
                    event.target.value
                  )
                }
                onBlur={() => {
                  setFieldError(
                    'password',
                    getPasswordError(
                      formData.password
                    )
                  );
                }}
                className={`input-field pr-10 ${
                  errors.password
                    ? 'border-red-300'
                    : ''
                }`}
                type={
                  showPassword
                    ? 'text'
                    : 'password'
                }
              />

              <button
                type="button"
                onClick={() =>
                  setShowPassword(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={
                  showPassword
                    ? 'Masquer le mot de passe'
                    : 'Afficher le mot de passe'
                }
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>

            {errors.password && (
              <p
                className="text-xs text-red-600 mt-1"
                role="alert"
              >
                {
                  errors.password
                }
              </p>
            )}

            {!errors.password &&
              formData.password &&
              PASSWORD_PATTERN.test(
                formData.password
              ) &&
              formData.password.length >=
                8 && (
                <p className="text-xs text-green-600 mt-1">
                  Mot de passe valide.
                </p>
              )}
          </div>

          {/* CONFIRM PASSWORD */}

          <div>
            <label className="block text-sm font-medium mb-1">
              {t(
                'auth.register.confirmPassword'
              )}{' '}

              <span className="text-red-500">
                *
              </span>
            </label>

            <div className="relative">
              <input
                value={
                  formData.confirmPassword
                }
                onChange={(
                  event
                ) =>
                  handleConfirmPasswordChange(
                    event.target.value
                  )
                }
                onBlur={() => {
                  if (
                    !formData.confirmPassword
                  ) {
                    setFieldError(
                      'confirmPassword',
                      t(
                        'auth.register.errors.required'
                      )
                    );
                  }
                }}
                className={`input-field pr-10 ${
                  errors.confirmPassword
                    ? 'border-red-300'
                    : ''
                }`}
                type={
                  showConfirmPassword
                    ? 'text'
                    : 'password'
                }
              />

              <button
                type="button"
                onClick={() =>
                  setShowConfirmPassword(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={
                  showConfirmPassword
                    ? 'Masquer la confirmation'
                    : 'Afficher la confirmation'
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>

            {errors.confirmPassword && (
              <p
                className="text-xs text-red-600 mt-1"
                role="alert"
              >
                {
                  errors.confirmPassword
                }
              </p>
            )}

            {!errors.confirmPassword &&
              formData.confirmPassword &&
              formData.password ===
                formData.confirmPassword && (
                <p className="text-xs text-green-600 mt-1">
                  Les mots de passe correspondent.
                </p>
              )}
          </div>
        </div>

        {showGuardian &&
          formData.legalGuardian && (
            <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
              <h3 className="font-semibold text-lg">
                {t(
                  'customerDetails.guest.legalGuardian'
                )}{' '}

                <span className="text-red-500">
                  *
                </span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* GUARDIAN FIRST NAME */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.guardianFirstName'
                    )}{' '}

                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      formData
                        .legalGuardian
                        .firstName
                    }
                    onChange={(
                      event
                    ) => {
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          firstName:
                            event.target.value,
                        }
                      );

                      removeError(
                        'legalGuardian.firstName'
                      );
                    }}
                    onBlur={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          firstName:
                            event.target.value.trim(),
                        }
                      )
                    }
                    className={`input-field ${
                      errors[
                        'legalGuardian.firstName'
                      ]
                        ? 'border-red-300'
                        : ''
                    }`}
                    type="text"
                  />

                  {errors[
                    'legalGuardian.firstName'
                  ] && (
                    <p className="text-xs text-red-600 mt-1">
                      {
                        errors[
                          'legalGuardian.firstName'
                        ]
                      }
                    </p>
                  )}
                </div>

                {/* GUARDIAN LAST NAME */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.guardianLastName'
                    )}{' '}

                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      formData
                        .legalGuardian
                        .lastName
                    }
                    onChange={(
                      event
                    ) => {
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          lastName:
                            event.target.value,
                        }
                      );

                      removeError(
                        'legalGuardian.lastName'
                      );
                    }}
                    onBlur={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          lastName:
                            event.target.value.trim(),
                        }
                      )
                    }
                    className={`input-field ${
                      errors[
                        'legalGuardian.lastName'
                      ]
                        ? 'border-red-300'
                        : ''
                    }`}
                    type="text"
                  />

                  {errors[
                    'legalGuardian.lastName'
                  ] && (
                    <p className="text-xs text-red-600 mt-1">
                      {
                        errors[
                          'legalGuardian.lastName'
                        ]
                      }
                    </p>
                  )}
                </div>

                {/* GUARDIAN EMAIL */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.guardianEmail'
                    )}{' '}

                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      formData
                        .legalGuardian
                        .email
                    }
                    onChange={(
                      event
                    ) => {
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          email:
                            event.target.value,
                        }
                      );

                      removeError(
                        'legalGuardian.email'
                      );
                    }}
                    onBlur={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          email:
                            event.target.value.trim(),
                        }
                      )
                    }
                    className={`input-field ${
                      errors[
                        'legalGuardian.email'
                      ]
                        ? 'border-red-300'
                        : ''
                    }`}
                    type="email"
                  />

                  {errors[
                    'legalGuardian.email'
                  ] && (
                    <p className="text-xs text-red-600 mt-1">
                      {
                        errors[
                          'legalGuardian.email'
                        ]
                      }
                    </p>
                  )}
                </div>

                {/* GUARDIAN INTERNATIONAL PHONE */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.guardianPhone'
                    )}{' '}

                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <InternationalPhoneInput
                    value={
                      guardianPhoneDisplay
                    }
                    country={
                      guardianPhoneCountry
                    }
                    required
                    onChange={(
                      result
                    ) => {
                      setGuardianPhoneDisplay(
                        result.displayValue
                      );

                      setGuardianPhoneCountry(
                        result.country
                      );

                      setGuardianPhoneE164(
                        result.e164Value
                      );

                      setGuardianPhoneIsValid(
                        result.isValid
                      );

                      setFormData(
                        (
                          previous
                        ) => {
                          if (
                            !previous.legalGuardian
                          ) {
                            return previous;
                          }

                          return {
                            ...previous,

                            legalGuardian: {
                              ...previous.legalGuardian,

                              phone:
                                result.displayValue,
                            },
                          };
                        }
                      );

                      if (
                        result.isValid
                      ) {
                        removeError(
                          'legalGuardian.phone'
                        );
                      }
                    }}
                    selectClassName="input-field"
                    inputClassName={`input-field ${
                      errors[
                        'legalGuardian.phone'
                      ]
                        ? 'border-red-300'
                        : ''
                    }`}
                    error={
                      errors[
                        'legalGuardian.phone'
                      ]
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* RELATIONSHIP */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.relationship'
                    )}
                  </label>

                  <select
                    value={
                      formData
                        .legalGuardian
                        .relationshipToMinor
                    }
                    onChange={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          relationshipToMinor:
                            event.target.value as LegalGuardianData['relationshipToMinor'],
                        }
                      )
                    }
                    className="input-field"
                  >
                    <option value="parent">
                      {t(
                        'customerDetails.guest.relationshipOptions.parent'
                      )}
                    </option>

                    <option value="legal_guardian">
                      {t(
                        'customerDetails.guest.relationshipOptions.legalGuardian'
                      )}
                    </option>
                  </select>
                </div>

                {/* DETAILS */}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t(
                      'customerDetails.guest.relationshipDetails'
                    )}
                  </label>

                  <input
                    value={
                      formData
                        .legalGuardian
                        .relationshipDetails ||
                      ''
                    }
                    onChange={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          relationshipDetails:
                            event.target.value,
                        }
                      )
                    }
                    onBlur={(
                      event
                    ) =>
                      handleChange(
                        'legalGuardian',
                        {
                          ...formData.legalGuardian!,

                          relationshipDetails:
                            event.target.value.trim(),
                        }
                      )
                    }
                    className="input-field"
                    type="text"
                  />
                </div>
              </div>
            </div>
          )}

        {/*
         * =====================================================
         * LEGAL CONSENTS
         * =====================================================
         */}

        <div className="space-y-3">
          {/* TERMS */}

          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={
                formData.acceptTerms
              }
              onChange={(
                event
              ) =>
                handleChange(
                  'acceptTerms',
                  event.target.checked
                )
              }
              className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded"
            />

            <span>
              <Trans
                i18nKey="piercingBooking.step5.termsConsent"
                components={{
                  termsLink: (
                    <button
                      type="button"
                      className="font-medium text-primary-600 underline hover:text-primary-700"
                      onClick={(
                        event
                      ) => {
                        event.preventDefault();

                        event.stopPropagation();

                        setOpenLegalModal(
                          'terms'
                        );
                      }}
                    />
                  ),
                }}
              />
            </span>
          </label>

          {errors.acceptTerms && (
            <p
              className="text-xs text-red-600"
              role="alert"
            >
              {
                errors.acceptTerms
              }
            </p>
          )}

          {/* PRIVACY */}

          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={
                formData.acceptPrivacy
              }
              onChange={(
                event
              ) =>
                handleChange(
                  'acceptPrivacy',
                  event.target.checked
                )
              }
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />

            <span>
              <LegalPrivacyLabel />
            </span>
          </label>

          {errors.acceptPrivacy && (
            <p
              className="text-xs text-red-600"
              role="alert"
            >
              {
                errors.acceptPrivacy
              }
            </p>
          )}
        </div>

        {/* BUTTONS */}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={
              handleSubmit
            }
            className="btn-primary flex items-center gap-2"
            disabled={
              loading ||
              tattooMinorBlocked
            }
          >
            {loading && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}

            <span>
              {loading
                ? t(
                    'auth.register.processing'
                  )
                : t(
                    'auth.register.registerButton'
                  )}
            </span>
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={
                onCancel
              }
              className="btn-secondary"
            >
              {t(
                'clientForm.cancel'
              )}
            </button>
          )}
        </div>
      </fieldset>

      {/*
       * =====================================================
       * TERMS MODAL
       * =====================================================
       *
       * Même principe que PiercingBooking.
       */}

      {openLegalModal ===
        'terms' && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-terms-modal-title"
          onMouseDown={() =>
            setOpenLegalModal(
              null
            )
          }
        >
          <div
            className="modal-panel flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <ModalHeader
              title={
                <h2
                  id="account-terms-modal-title"
                  className="text-xl font-semibold text-textPrimary"
                >
                  {t(
                    'legal.pages.terms.title'
                  )}
                </h2>
              }
              onClose={() =>
                setOpenLegalModal(
                  null
                )
              }
              className="border-b border-gray-200 px-5 py-4 sm:px-6"
              closeClassName="mt-0 mr-0"
            />

            <div className="overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              <LegalPageContent
                page="terms"
                embedded
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}